class HbCarousel extends HbElements.Component {
  onConnect() {
    if (typeof Swiper === 'undefined') return;

    const swiperEl = this.querySelector('.swiper');
    if (!swiperEl) return;

    const slides = swiperEl.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide');
    if (slides.length < 1) return;

    const autoplay = this.boolAttr('auto');
    const loop = this.boolAttr('loop') && slides.length > 1;
    const centeredSlides = this.boolAttr('centerMode');
    const slidesPerView = this.numAttr('slidesPerView', 1);
    const spaceBetween = this.numAttr('spaceBetween', 0);
    const delay = Math.max(this.numAttr('autoplayDelay', 7500), 1000);
    const breakpoints = this.jsonAttr('breakpoints', {});
    const paginationEl = swiperEl.querySelector('.swiper-pagination');
    const prevEl = this.navEl('prev');
    const nextEl = this.navEl('next');

    const options = {
      slidesPerView,
      spaceBetween,
      centeredSlides,
      loop,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      breakpoints,
    };

    if (autoplay && slides.length > 1) {
      options.autoplay = {
        delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    if (paginationEl && slides.length > 1) {
      options.pagination = {
        el: paginationEl,
        clickable: true,
      };
    }

    if (prevEl && nextEl && slides.length > 1) {
      options.navigation = { prevEl, nextEl };
    }

    this.swiper = new Swiper(swiperEl, options);
  }

  navEl(direction) {
    const selector = `[data-hb-carousel-${direction}]`;
    return this.querySelector(selector) || this.closest('.shopify-section')?.querySelector(selector);
  }

  onDisconnect() {
    if (!this.swiper) return;
    this.swiper.destroy(true, true);
    this.swiper = null;
  }
}

HbElements.define('hb-carousel', HbCarousel);

class HbProductMedia extends HbElements.Component {
  onConnect() {
    if (typeof Swiper === 'undefined') return;

    this.productId = this.dataset.productId || '';
    this.mainEl = this.querySelector('[data-main]');
    this.thumbsEl = this.querySelector('[data-thumbs]');
    this.mainWrapper = this.mainEl ? this.mainEl.querySelector('.swiper-wrapper') : null;
    this.thumbsWrapper = this.thumbsEl ? this.thumbsEl.querySelector('.swiper-wrapper') : null;
    this.variantMedia = {};
    this.groupingData = null;
    this.groupingEnabled = false;
    this.optionIndex = -1;
    this.mediaByOptionValue = {};
    this.sharedMediaIds = [];
    this.firstFeaturedIndex = -1;
    this.previousOptionValue = null;
    this.cachedMainSlides = [];
    this.cachedThumbSlides = [];
    this.spaceBetween = this.numAttr('spaceBetween', 0);
    this.spaceBetweenDesktop = this.numAttr('spaceBetweenDesktop', this.spaceBetween);
    this.breakpoint = this.numAttr('breakpoint', 768);

    const mapNode = this.querySelector('[data-variant-media]');
    if (mapNode) {
      try {
        this.variantMedia = JSON.parse(mapNode.textContent);
      } catch (error) {
        this.variantMedia = {};
      }
    }

    this.initGrouping();

    const initialVariantId = this.dataset.initialVariantId;
    const initialVariant = this.findVariant(initialVariantId);
    if (this.groupingEnabled && initialVariant) {
      this.applyGrouping(initialVariant, true);
    }

    this.initSwipers();

    this.onVariantChange = this.onVariantChange.bind(this);
    document.addEventListener('hb:variant-change', this.onVariantChange);

    if (initialVariantId) {
      this.slideToVariant(initialVariantId);
    }
  }

  onDisconnect() {
    document.removeEventListener('hb:variant-change', this.onVariantChange);
    this.destroySwipers();
  }

  initGrouping() {
    const enabledAttr = this.dataset.variantImageGrouping;
    if (enabledAttr !== 'true') return;

    const groupingNode = this.querySelector('[data-media-grouping]');
    if (!groupingNode) return;

    try {
      this.groupingData = JSON.parse(groupingNode.textContent);
    } catch (error) {
      this.groupingData = null;
      return;
    }

    const data = this.groupingData;
    if (!data || !Array.isArray(data.media) || data.media.length <= 1) return;
    if (!Array.isArray(data.variants) || data.variants.length <= 1) return;
    if (!Array.isArray(data.options) || data.options.length < 1) return;

    const optionNames = (this.dataset.variantImageGroupingOption || '')
      .split(',')
      .map((name) => name.trim().toLowerCase())
      .filter(Boolean);
    const productOptions = data.options.map((name) => String(name).toLowerCase());
    this.optionIndex = -1;
    for (const optionName of optionNames) {
      const index = productOptions.indexOf(optionName);
      if (index > -1) {
        this.optionIndex = index;
        break;
      }
    }
    if (this.optionIndex < 0) return;

    this.mediaByOptionValue = {};
    data.variants.forEach((variant) => {
      if (variant.featured_media_id == null) return;
      const mediaId = String(variant.featured_media_id);
      if (!this.mediaByOptionValue[mediaId]) {
        this.mediaByOptionValue[mediaId] = [];
      }
      const value = variant.options && variant.options[this.optionIndex];
      if (value != null) {
        this.mediaByOptionValue[mediaId].push(value);
      }
    });

    this.sharedMediaIds = [];
    let firstFeaturedIndex = -1;
    data.media.forEach((mediaId, index) => {
      if (!this.mediaByOptionValue[String(mediaId)]) return;
      if (firstFeaturedIndex < 0) firstFeaturedIndex = index;
    });
    this.firstFeaturedIndex = firstFeaturedIndex;

    if (firstFeaturedIndex > 0) {
      this.sharedMediaIds = data.media.slice(0, firstFeaturedIndex).map((id) => String(id));
    }

    if (this.mainWrapper) {
      this.cachedMainSlides = Array.from(this.mainWrapper.querySelectorAll(':scope > .swiper-slide[data-media-id]'));
    }
    if (this.thumbsWrapper) {
      this.cachedThumbSlides = Array.from(this.thumbsWrapper.querySelectorAll(':scope > .swiper-slide[data-media-id]'));
    }

    this.groupingEnabled = true;
  }

  findVariant(variantId) {
    if (!this.groupingData || !Array.isArray(this.groupingData.variants)) return null;
    return this.groupingData.variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  slideByMediaId(cache, mediaId) {
    return cache.find((slide) => String(slide.dataset.mediaId) === String(mediaId)) || null;
  }

  /**
   * Bucket = media from this option's featured image until the next featured.
   * Shared = media before the first featured image.
   * Display order: featured → shared angles → rest of bucket.
   */
  visibleMediaIds(optionValue) {
    const bucket = [];
    let currentValues = null;

    this.groupingData.media.forEach((mediaId) => {
      const id = String(mediaId);
      if (this.mediaByOptionValue[id]) {
        currentValues = this.mediaByOptionValue[id];
      }
      if (!currentValues) return;
      if (currentValues.includes(optionValue)) {
        bucket.push(id);
      }
    });

    if (!bucket.length) return [...(this.sharedMediaIds || [])];

    const featuredId = bucket.find((id) => this.mediaByOptionValue[id]) || bucket[0];
    const rest = bucket.filter((id) => id !== featuredId);
    const shared = (this.sharedMediaIds || []).filter((id) => id !== featuredId);
    return [featuredId, ...shared, ...rest];
  }

  applyGrouping(variant, skipPreviousCheck) {
    if (!this.groupingEnabled || !variant || !variant.options) return false;

    const optionValue = variant.options[this.optionIndex];
    if (optionValue == null) return false;
    if (!skipPreviousCheck && this.previousOptionValue === optionValue) return false;

    const visibleIds = this.visibleMediaIds(optionValue);

    if (this.mainWrapper) {
      this.mainWrapper.innerHTML = '';
      visibleIds.forEach((mediaId) => {
        const slide = this.slideByMediaId(this.cachedMainSlides, mediaId);
        if (slide) this.mainWrapper.appendChild(slide);
      });
    }

    if (this.thumbsWrapper) {
      this.thumbsWrapper.innerHTML = '';
      visibleIds.forEach((mediaId) => {
        const slide = this.slideByMediaId(this.cachedThumbSlides, mediaId);
        if (slide) this.thumbsWrapper.appendChild(slide);
      });
    }

    this.previousOptionValue = optionValue;
    this.updateThumbsVisibility(visibleIds.length);
    return true;
  }

  updateThumbsVisibility(visibleCount) {
    if (!this.thumbsEl) return;
    const show = visibleCount > 1;
    this.thumbsEl.hidden = !show;
    this.thumbsEl.style.display = show ? '' : 'none';
  }

  initSwipers() {
    this.destroySwipers();

    const thumbsOptions = {
      slidesPerView: 'auto',
      spaceBetween: this.spaceBetween,
      watchSlidesProgress: true,
      watchOverflow: true,
      breakpoints: {
        [this.breakpoint]: { spaceBetween: this.spaceBetweenDesktop },
      },
    };

    if (this.thumbsEl && !this.thumbsEl.hidden) {
      const thumbSlides = this.thumbsWrapper
        ? this.thumbsWrapper.querySelectorAll(':scope > .swiper-slide')
        : [];
      if (thumbSlides.length > 1) {
        this.thumbsSwiper = new Swiper(this.thumbsEl, thumbsOptions);
      }
    }

    if (this.mainEl) {
      const mainOptions = {
        slidesPerView: 1,
        spaceBetween: 0,
        watchOverflow: true,
        observer: true,
        observeParents: true,
      };
      if (this.thumbsSwiper) {
        mainOptions.thumbs = { swiper: this.thumbsSwiper };
      }
      this.mainSwiper = new Swiper(this.mainEl, mainOptions);
    }
  }

  destroySwipers() {
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
  }

  onVariantChange(event) {
    const detail = event.detail || {};
    if (this.productId && detail.productId && String(detail.productId) !== String(this.productId)) {
      return;
    }
    const variantId = detail.variantId != null ? String(detail.variantId) : '';
    if (!variantId) return;

    let variant = detail.variant || null;
    if (this.groupingEnabled) {
      const groupingVariant = this.findVariant(variantId);
      if (groupingVariant) {
        const regrouped = this.applyGrouping(groupingVariant, false);
        if (regrouped) {
          this.initSwipers();
        }
      }
    }

    this.slideToVariant(variantId, variant);
  }

  slideToVariant(variantId, variant) {
    if (!this.mainSwiper || !this.mainEl) return;

    let mediaId = this.variantMedia[String(variantId)];
    if (mediaId == null && variant && variant.featured_media) {
      mediaId = variant.featured_media.id;
    }
    if (mediaId == null && this.groupingEnabled) {
      const groupingVariant = this.findVariant(variantId);
      if (groupingVariant) mediaId = groupingVariant.featured_media_id;
    }
    if (mediaId == null) return;

    const slides = Array.from(this.mainEl.querySelectorAll('.swiper-slide[data-media-id]'));
    const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
    if (index < 0) return;
    this.mainSwiper.slideTo(index);
  }
}

HbElements.define('hb-product-media', HbProductMedia);

class HbFeaturedProducts extends HbElements.Component {
  onConnect() {
    if (typeof Swiper === 'undefined') return;

    this.mainEl = this.querySelector('[data-main]');
    this.thumbsEl = this.querySelector('[data-thumbs]');
    this.pills = Array.from(this.querySelectorAll('.featured-products__pill'));
    this.copyData = this.jsonAttr('copy', []);
    this.copyNodes = this.querySelectorAll('[data-fp-copy] [id^="ct_"]');
    this.loop = this.boolAttr('loop');
    this.spaceBetween = this.numAttr('spaceBetween', 0);
    this.breakpoint = this.numAttr('breakpoint', 768);
    this.mobileSlideWidth = this.numAttr('mobileSlideWidth', 120);
    this.activeIndex = 0;
    this.isDesktop = false;

    this.onPillClick = this.onPillClick.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.onProductLinkClick = this.onProductLinkClick.bind(this);

    this.pills.forEach((pill) => {
      pill.addEventListener('click', this.onPillClick);
    });

    if (this.mainEl) {
      this.mainEl.addEventListener('click', this.onProductLinkClick);
    }

    this.mq = window.matchMedia(`(min-width: ${this.breakpoint}px)`);
    if (this.mq.addEventListener) {
      this.mq.addEventListener('change', this.onBreakpointChange);
    } else if (this.mq.addListener) {
      this.mq.addListener(this.onBreakpointChange);
    }

    this.initSwipers(this.mq.matches);
    this.updateCopy(this.activeIndex);
  }

  onDisconnect() {
    this.pills.forEach((pill) => {
      pill.removeEventListener('click', this.onPillClick);
    });
    if (this.mainEl) {
      this.mainEl.removeEventListener('click', this.onProductLinkClick);
    }
    if (this.mq) {
      if (this.mq.removeEventListener) {
        this.mq.removeEventListener('change', this.onBreakpointChange);
      } else if (this.mq.removeListener) {
        this.mq.removeListener(this.onBreakpointChange);
      }
    }
    this.destroySwipers();
  }

  onBreakpointChange(event) {
    const matches = event.matches != null ? event.matches : this.mq.matches;
    this.initSwipers(matches);
  }

  destroySwipers() {
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
    this.clearMobileClones();
  }

  clearMobileClones() {
    if (!this.mainEl) return;
    this.mainEl.querySelectorAll('.swiper-slide[data-fp-clone]').forEach((el) => el.remove());
  }

  /**
   * Build 3 copies of the product slides. Start in the middle copy so
   * centeredSlides always has neighbors left/right (no Swiper loop needed).
   */
  prepareMobileSlides() {
    if (!this.mainEl) return { uniqueCount: 0, startOffset: 0 };
    this.clearMobileClones();

    const wrapper = this.mainEl.querySelector(':scope > .swiper-wrapper');
    if (!wrapper) return { uniqueCount: 0, startOffset: 0 };

    const originals = Array.from(wrapper.querySelectorAll(':scope > .swiper-slide'));
    const uniqueCount = originals.length;
    if (uniqueCount < 2) return { uniqueCount, startOffset: 0 };

    // Append two extra full sets → 3 total copies.
    for (let copy = 0; copy < 2; copy += 1) {
      originals.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-fp-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        const link = clone.querySelector('a.featured-products__product-link');
        if (link) link.setAttribute('tabindex', '-1');
        wrapper.appendChild(clone);
      });
    }

    return { uniqueCount, startOffset: uniqueCount };
  }

  getActiveProductIndex(swiper) {
    const slide = swiper?.slides?.[swiper.activeIndex];
    if (!slide) return 0;
    const index = Number(slide.dataset.index);
    return Number.isFinite(index) ? index : 0;
  }

  /**
   * Keep the active slide inside the middle copy so peeks never run out.
   */
  keepMobileInMiddle(swiper) {
    if (!swiper || this.isDesktop || !this.mobileWrapped || !this.uniqueCount) return;
    const u = this.uniqueCount;
    const i = swiper.activeIndex;
    if (i < u) {
      swiper.slideTo(i + u, 0, false);
    } else if (i >= u * 2) {
      swiper.slideTo(i - u, 0, false);
    }
  }

  initSwipers(isDesktop) {
    if (!this.mainEl) return;

    this.destroySwipers();
    this.isDesktop = Boolean(isDesktop);
    this.classList.toggle('is-desktop', this.isDesktop);
    this.classList.toggle('is-mobile', !this.isDesktop);

    const slides = this.mainEl.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide');
    if (slides.length < 1) return;

    const productIndex = this.activeIndex || 0;
    this.uniqueCount = slides.length;
    this.mobileWrapped = false;
    let initialSlide = productIndex;

    if (!this.isDesktop && this.loop && this.uniqueCount > 1) {
      const prepared = this.prepareMobileSlides();
      this.uniqueCount = prepared.uniqueCount;
      this.mobileWrapped = prepared.startOffset > 0;
      initialSlide = prepared.startOffset + productIndex;
    }

    if (this.isDesktop && this.thumbsEl) {
      this.thumbsSwiper = new Swiper(this.thumbsEl, {
        slidesPerView: 'auto',
        spaceBetween: this.spaceBetween,
        watchSlidesProgress: true,
        watchOverflow: true,
      });
    }

    const mainOptions = {
      initialSlide,
      on: {
        slideChange: (swiper) => {
          this.setActivePill(this.getActiveProductIndex(swiper));
        },
        slideChangeTransitionEnd: (swiper) => {
          this.keepMobileInMiddle(swiper);
        },
      },
    };

    if (this.isDesktop) {
      mainOptions.slidesPerView = 1;
      mainOptions.spaceBetween = 0;
      mainOptions.watchOverflow = true;
      mainOptions.loop = false;
      if (this.thumbsSwiper) {
        mainOptions.thumbs = { swiper: this.thumbsSwiper };
      }
    } else {
      // No Swiper loop — middle-copy start gives left/right neighbors on init.
      mainOptions.slidesPerView = 3.4;
      mainOptions.centeredSlides = true;
      mainOptions.spaceBetween = 8;
      mainOptions.watchOverflow = false;
      mainOptions.slideToClickedSlide = true;
      mainOptions.resistanceRatio = 0.85;
      mainOptions.loop = false;
      mainOptions.rewind = false;
      mainOptions.observer = false;
      mainOptions.observeParents = false;
    }

    this.mainSwiper = new Swiper(this.mainEl, mainOptions);
    this.setActivePill(this.getActiveProductIndex(this.mainSwiper));

    // Force layout after images/fonts settle so centered offset is correct.
    requestAnimationFrame(() => {
      if (!this.mainSwiper) return;
      this.mainSwiper.update();
      this.mainSwiper.slideTo(initialSlide, 0, false);
      this.keepMobileInMiddle(this.mainSwiper);
      this.setActivePill(this.getActiveProductIndex(this.mainSwiper));
    });
  }

  onProductLinkClick(event) {
    const link = event.target.closest('a.featured-products__product-link');
    if (!link || !this.mainEl.contains(link)) return;

    const slide = link.closest('.swiper-slide');
    if (!slide) return;

    if (!slide.classList.contains('swiper-slide-active')) {
      event.preventDefault();
    }
  }

  onPillClick(event) {
    const pill = event.currentTarget;
    const index = Number(pill.dataset.index);
    if (!Number.isFinite(index) || !this.mainSwiper) return;

    if (index !== this.activeIndex) {
      event.preventDefault();
      if (!this.isDesktop && this.mobileWrapped) {
        this.mainSwiper.slideTo(this.uniqueCount + index);
      } else {
        this.mainSwiper.slideTo(index);
      }
      this.setActivePill(index);
    }
  }

  setActivePill(index) {
    this.activeIndex = index;
    this.pills.forEach((pill) => {
      const pillIndex = Number(pill.dataset.index);
      const selected = pillIndex === index;
      pill.classList.toggle('is-selected', selected);
      pill.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (selected && !this.isDesktop && typeof pill.scrollIntoView === 'function') {
        pill.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
      }
    });
    this.updateCopy(index);
  }

  setCopyText(el, text) {
    if (!el || text == null || text === '') return;
    const target = el.querySelector('p') || el;
    if (target.textContent === text) return;
    target.textContent = text;
  }

  updateCopy(index) {
    if (!this.copyNodes || this.copyNodes.length < 3) return;
    if (!Array.isArray(this.copyData) || !this.copyData[index]) return;

    const entry = this.copyData[index];
    this.setCopyText(this.copyNodes[0], entry.eyebrow);
    this.setCopyText(this.copyNodes[1], entry.headline);
    this.setCopyText(this.copyNodes[2], entry.subline);
  }
}

HbElements.define('hb-featured-products', HbFeaturedProducts);
